import {
  Button,
  ComposedModal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@carbon/react';
import { useAtom } from 'jotai';

import { customLossWarningAtom } from '../atoms';
import styles from './custom-loss-warning-modal.module.scss';

/**
 * Warns before re-certifying interests to Allocation Manager that were
 * previously certified with Custom values (`user_entered`) — switching the
 * source overwrites those entered values (INS-9897). Atom-driven open state
 * mirrors the Unconfirm modal: the panel writes `{ count, onConfirm }` to open
 * it and the deferred `onConfirm` resumes the original submit. Cancel aborts.
 *
 * @return {JSX.Element} The rendered modal (hidden when no warning is pending).
 */
export const CustomLossWarningModal = (): JSX.Element => {
  const [warning, setWarning] = useAtom(customLossWarningAtom);

  const handleClose = (): void => {
    setWarning(null);
  };

  const handleConfirm = (): void => {
    warning?.onConfirm();
    setWarning(null);
  };

  const count = warning?.count ?? 0;
  const interestNoun = count === 1 ? 'interest' : 'interests';

  return (
    <ComposedModal
      open={warning !== null}
      onClose={handleClose}
      preventCloseOnClickOutside
      size="sm"
    >
      <ModalHeader title="Overwrite Custom values?" />
      <ModalBody>
        <p className={styles.body}>
          You previously entered Custom values for{' '}
          {count > 0 ? <strong>{count}</strong> : 'some'} of these{' '}
          {interestNoun}. Switching to Allocation Manager will overwrite those
          values. Are you sure you want to continue?
        </p>
      </ModalBody>
      <ModalFooter>
        <Button kind="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button kind="primary" onClick={handleConfirm}>
          Switch to Allocation Manager
        </Button>
      </ModalFooter>
    </ComposedModal>
  );
};
